import {
  Input as GSInput,
  InputField,
  InputSlot,
  FormControl,
  Spinner as UISpinner
} from "@gluestack-ui/themed";
import { InputIcon } from "./components/InputIcon";
import { InputHelperText } from "./components/InputHelperText";
import { InputError } from "./components/InputError";
import { InputLabel } from "./components/InputLabel";
import { InputProps } from "./types";
import { useInputController } from "./useInputController";

import { iconStyle } from "./styles";

export const Input = ({
  label,
  isLoading,
  leftIcon,
  rightIcon,
  placeholder,
  mask,
  isCurrency,
  useMaskValue,
  helperText,
  secureTextEntry,
  errorMessage,
  showClear,
  enlargeClearButton,
  value,
  onChange,
  transformValue,
  isDisabled,
  isInvalid,
  isReadOnly,
  isRequired,
  isBigInput,
  light,
  onClear,
  keyboardType,
  inputMode,
  onSubmitEditing,
  autoCorrect,
  spellCheck,
  textContentType,
  autoComplete,
  maxLength,
  importantForAutofill,
  onBlur,
  onKeyPress,
  returnKeyType,
  autoCapitalize,
  contextMenuHidden = false,
  enterKeyHint = "done",
  ...inputProps
}: InputProps) => {
  const {
    toogleShowPassword,
    showPassword,
    parsedInputValue,
    handleOnChangeInput,
    handleClear,
    bigInputStyles
  } = useInputController({
    value,
    onClear,
    onChange,
    mask,
    isCurrency,
    isBigInput,
    useMaskValue,
    transformValue
  });

  return (
    <FormControl
      size="md"
      $required-bgColor="blue"
      isDisabled={isDisabled || isLoading}
      isInvalid={isInvalid || !!errorMessage}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      flex={inputProps.flex}
      width={inputProps.width ?? "$full"}
      alignSelf="stretch"
    >
      <InputLabel label={label} light={light} />
      <GSInput {...inputProps}>
        {leftIcon?.name ? (
          <InputSlot pl="$3">
            <InputIcon w="$full" {...leftIcon} size={leftIcon.size || "xs"} />
          </InputSlot>
        ) : null}
        <InputField
          placeholderTextColor="$textLight500"
          type={!showPassword && secureTextEntry ? "password" : "text"}
          placeholder={placeholder}
          autoCorrect={autoCorrect}
          spellCheck={spellCheck}
          textContentType={textContentType}
          autoComplete={autoComplete}
          onBlur={onBlur}
          onSubmitEditing={onSubmitEditing}
          onKeyPress={onKeyPress}
          returnKeyType={returnKeyType}
          maxLength={maxLength}
          importantForAutofill={importantForAutofill}
          keyboardType={keyboardType}
          inputMode={inputMode}
          autoCapitalize={autoCapitalize}
          onChangeText={handleOnChangeInput}
          value={parsedInputValue}
          contextMenuHidden={contextMenuHidden}
          enterKeyHint={enterKeyHint}
          {...bigInputStyles}
        />
        {secureTextEntry && (
          <InputSlot
            hitSlop={iconStyle.hitStop}
            zIndex={10}
            cursor="pointer"
            pr="$4"
            pl="$2"
            onPress={toogleShowPassword}
          >
            <InputIcon
              w="$full"
              name={showPassword ? "eye-slash" : "eye"}
              size="xs"
            />
          </InputSlot>
        )}

        {!secureTextEntry && isLoading && (
          <InputSlot pr="$4" zIndex={20}>
            <UISpinner size="small" color="$primary600" />
          </InputSlot>
        )}

        {!secureTextEntry && !isLoading && rightIcon?.name && (
          <InputSlot
            pr={showClear && parsedInputValue ? "$2" : "$4"}
            zIndex={20}
          >
            <InputIcon w="$full" {...rightIcon} size={rightIcon.size || "xs"} />
          </InputSlot>
        )}

        {!secureTextEntry && !isLoading && showClear && parsedInputValue ? (
          <InputSlot
            cursor="pointer"
            pr="$4"
            pl={enlargeClearButton ? "$3" : undefined}
            minWidth={enlargeClearButton ? 44 : undefined}
            minHeight={enlargeClearButton ? 44 : undefined}
            justifyContent="center"
            alignItems="center"
            hitSlop={
              enlargeClearButton ? iconStyle.enlargedHitSlop : iconStyle.hitStop
            }
            onPress={handleClear}
          >
            <InputIcon name="times-circle" size="2sm" />
          </InputSlot>
        ) : null}
      </GSInput>
      <InputHelperText text={helperText} hasError={!!errorMessage} />
      <InputError errorMessage={errorMessage} />
    </FormControl>
  );
};
